import { ShareButton } from '@/components/changelog/share-button'
import { ChangelogPageShell } from '@/components/changelog/changelog-page-shell'
import { TrendsDrawer } from '@/components/trends/trends-drawer'
import { Logo } from '@/components/logo'
import { Button } from '@/components/ui/button'
import type { ChangelogEntry } from '@/types/changelog'
import type { ChangelogShow } from '@/types'
import { Head, Link } from '@inertiajs/react'
import { Rss } from 'lucide-react'

type Props = ChangelogShow

export default function ChangelogShow({ entries, groupedEntries, commitsProcessed, trends }: Props) {
  const hero = (
    <>
      <nav className="text-muted-foreground mb-8 text-sm">
        <Link href="/" className="hover:text-foreground">
          Home
        </Link>
        <span className="mx-2">/</span>
        <span className="text-foreground">Changelog</span>
      </nav>

      <div className="flex flex-col gap-8 lg:flex-row lg:items-start lg:justify-between">
        <div className="flex-1">
          <div className="mb-6 flex flex-wrap items-center gap-4">
            <Logo className="text-3xl" />
            {trends ? <TrendsDrawer trends={trends} /> : null}
          </div>

          <h1 className="from-foreground to-muted-foreground mb-6 bg-gradient-to-r bg-clip-text text-4xl text-transparent sm:text-5xl">
            Built to help you ship faster
          </h1>
          <p className="text-muted-foreground max-w-3xl text-lg sm:text-xl">
            See the latest features helping teams boost productivity.
          </p>
        </div>

        <div className="flex flex-wrap gap-3">
          <ShareButton />
          <a href="/changelog/rss.xml">
            <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-foreground">
              <Rss className="mr-2 h-4 w-4" />
              RSS Feed
            </Button>
          </a>
          <a href="https://x.com/RaulOnRails" target="_blank" rel="noopener noreferrer">
            <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-foreground">
              <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
              </svg>
              Follow
            </Button>
          </a>
        </div>
      </div>

      <div className="text-muted-foreground mt-12 grid gap-4 text-sm sm:grid-cols-3 sm:gap-6">
        <div className="flex items-center gap-3">
          <span className="text-2xl">🚀</span>
          <span>Helping teams save hours every week</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-2xl">⭐</span>
          <span>Trusted by leading companies</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-2xl">🤖</span>
          <span>Processed {commitsProcessed.toLocaleString()} code changes</span>
        </div>
      </div>
    </>
  )

  return (
    <>
      <Head title="Changelog">
        <meta
          head-key="description"
          name="description"
          content="Follow Git Digest product updates: team GitHub and Slack commit digests, standup replacements, and shipping improvements for engineering managers."
        />
        <link rel="alternate" type="application/rss+xml" title="Git Digest Changelog RSS Feed" href="/changelog/rss.xml" />
      </Head>
      <ChangelogPageShell
        hero={hero}
        entries={entries as ChangelogEntry[]}
        groupedEntries={groupedEntries as Record<string, ChangelogEntry[]>}
      />
    </>
  )
}
